import React, { useState, useEffect } from "react";
import { BASE_URL } from "../../constants";
import { apiFetch } from "../../utils/apiFetch";
import { useApiError } from "../../utils/useApiError";
import PageInfoModal from "../../Components/PageInfoModal/PageInfoModal";
import ValidationErrorModal from "../../Components/ValidationErrorModal/ValidationErrorModal";
import CopyFooter from "../../Components/CopyFooter/CopyFooter";
import ReloadButton from "../../Components/LoadingButtons/ReloadButton";
import SearchButton from "../../Components/LoadingButtons/SearchButton";
import DataLoader from "../../Components/Spinner/DataLoader";

const TRIAL_INFO_ITEMS = [
  { icon: "bi-calendar-range", text: "Başlanğıc və bitmə tarixini seçib «Axtar» düyməsini klikləyin." },
  { icon: "bi-person-check", text: "Cədvəldə seçilmiş tarix aralığında keçirdiyiniz sınaq dərslərinin tələbə siyahısı görünür." },
  { icon: "bi-arrow-clockwise", text: "Yenilə düyməsi ilə mövcud tarix aralığı üzrə məlumatı yenidən çəkə bilərsiniz." },
];

const getDefaultRange = () => {
  const today = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 1);
  const fmt = (d) => {
    const m = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    return `${d.getFullYear()}-${m}-${day}`;
  };
  return { beginDate: fmt(from), endDate: fmt(today) };
};

const TrialLessonsPage = () => {
  const [dateRange, setDateRange] = useState(getDefaultRange);
  const [loading, setLoading] = useState(false);
  const [trigger, setTrigger] = useState(null); // 'search' | 'reload'
  const [students, setStudents] = useState([]);
  const [copiedText, setCopiedText] = useState("");
  const { errorModal, clearError, handleApiError } = useApiError();

  const fetchData = async (src = "reload") => {
    setTrigger(src);
    setLoading(true);
    try {
      const [res] = await Promise.all([
        apiFetch(
          `${BASE_URL}/teacher-trial-lesson-statistics/my-trial-lesson-students`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(dateRange),
          }
        ),
        new Promise((r) => setTimeout(r, 600)),
      ]);
      if (!res.ok) {
        await handleApiError(res);
        return;
      }
      const data = await res.json();
      setStudents(data.students ?? []);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
      setTrigger(null);
    }
  };

  useEffect(() => { fetchData("reload"); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const inputStyle = {
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "6px 10px",
    fontSize: 14,
    outline: "none",
    background: "#fff",
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", backgroundColor: "#fcfcfc", padding: "12px 16px" }}>

      {/* Header card */}
      <div style={{
        background: "#fff",
        border: "1px solid #E5E7EB",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        borderRadius: 10,
        padding: "12px 16px",
        marginBottom: 12,
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <PageInfoModal title="Sınaq Dərslərim" items={TRIAL_INFO_ITEMS} />

          <div style={{ width: 34, height: 34, background: "#EFF6FF", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <i className="bi bi-person-check-fill" style={{ color: "#2563EB", fontSize: 16 }} />
          </div>
          <span style={{ fontWeight: 600, fontSize: 15, color: "#0F172A" }}>Sınaq Dərslərim</span>

          <div style={{ flex: 1 }} />

          <label style={{ fontSize: 13, color: "#6b7280", display: "flex", alignItems: "center", gap: 6 }}>
            Başlanğıc:
            <input
              type="date"
              style={inputStyle}
              value={dateRange.beginDate}
              onChange={(e) => setDateRange((p) => ({ ...p, beginDate: e.target.value }))}
            />
          </label>

          <label style={{ fontSize: 13, color: "#6b7280", display: "flex", alignItems: "center", gap: 6 }}>
            Bitmə:
            <input
              type="date"
              style={inputStyle}
              value={dateRange.endDate}
              onChange={(e) => setDateRange((p) => ({ ...p, endDate: e.target.value }))}
            />
          </label>

          <SearchButton onClick={() => fetchData("search")} loading={trigger === "search"} disabled={loading && trigger !== "search"} />
          <ReloadButton onClick={() => fetchData("reload")} loading={trigger === "reload"} disabled={loading && trigger !== "reload"} />
        </div>
      </div>

      {/* Summary badge */}
      {!loading && (
        <div style={{ marginBottom: 8, flexShrink: 0 }}>
          <span style={{ background: "#EFF6FF", color: "#2563EB", border: "1px solid #BFDBFE", borderRadius: 20, padding: "3px 12px", fontSize: 12.5, fontWeight: 600 }}>
            Cəmi: {students.length} tələbə
          </span>
        </div>
      )}

      {/* Table wrapper */}
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", position: "relative", background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, overflow: "hidden" }}>
        <DataLoader show={loading} />
        {!loading && (
          <div style={{ overflowX: "auto", overflowY: "auto", flex: 1, minHeight: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, color: "#374151" }}>
              <thead>
                <tr style={{ background: "#f3f4f6", position: "sticky", top: 0, zIndex: 5 }}>
                  {["#", "Tələbə kodu", "Tələbə adı", "Sınaq dərsi tarixi"].map((h) => (
                    <th key={h} style={{ padding: "9px 12px", textAlign: "left", borderBottom: "1px solid #E5E7EB", background: "#F8FAFC", color: "#64748B", fontWeight: 600, fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {students.map((s, i) => (
                  <tr
                    key={s.studentId ?? i}
                    style={{ borderBottom: "1px solid #e5e7eb", cursor: "pointer", transition: "background 0.15s", height: 35 }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "")}
                  >
                    <td style={{ padding: "6px 12px", whiteSpace: "nowrap" }} onClick={() => setCopiedText(String(i + 1))}>{i + 1}</td>
                    <td style={{ padding: "6px 12px", whiteSpace: "nowrap" }} onClick={() => setCopiedText(String(s.studentId))}>{s.studentId}</td>
                    <td style={{ padding: "6px 12px", whiteSpace: "nowrap" }} onClick={() => setCopiedText(s.studentFullName)}>{s.studentFullName}</td>
                    <td style={{ padding: "6px 12px", whiteSpace: "nowrap" }} onClick={() => setCopiedText(s.register)}>{s.register}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Copy footer */}
      <CopyFooter value={copiedText} />

      <ValidationErrorModal
        show={!!errorModal}
        onHide={clearError}
        title={errorModal?.title}
        message={errorModal?.message}
        errors={errorModal?.errors}
      />
    </div>
  );
};

export default TrialLessonsPage;
